import { useChat } from "../contexts/ChatContext";

const STYLES = {
  available:  { dot: "bg-emerald-500", ring: "ring-emerald-100", pill: "bg-emerald-50 text-emerald-700 border-emerald-200", label: "Available" },
  in_session: { dot: "bg-amber-500",   ring: "ring-amber-100",   pill: "bg-amber-50 text-amber-800 border-amber-200",    label: "In session" },
  away:       { dot: "bg-slate-400",   ring: "ring-slate-100",   pill: "bg-slate-50 text-slate-500 border-slate-200",    label: "Away" },
};

/**
 * Presence indicator with three explicit states — always shows an accompanying label
 * so users can never confuse it with the *session mode* (online vs in-person).
 *
 * Usage:
 *   <PresenceDot userId={c.user_id} />                 // counsellor by default
 *   <PresenceDot userId={c.user_id} role="client" />   // client presence
 *   <PresenceDot userId={c.user_id} compact />         // dot + tiny label (for cards)
 *   <PresenceDot userId={c.user_id} hideLabel />       // just the dot (rare)
 */
export default function PresenceDot({ userId, role = "counsellor", compact = false, hideLabel = false, className = "" }) {
  const { getCounsellorPresence, getClientPresence } = useChat() || {};
  const status = role === "client"
    ? (getClientPresence ? getClientPresence(userId) : "away")
    : (getCounsellorPresence ? getCounsellorPresence(userId) : "away");
  const s = STYLES[status] || STYLES.away;

  if (hideLabel) {
    return (
      <span
        aria-label={s.label}
        title={s.label}
        data-testid={`presence-dot-${userId}`}
        data-status={status}
        className={`inline-block ${s.dot} ring-2 ${s.ring} ${className}`}
        style={{ width: 10, height: 10, borderRadius: 999 }}
      />
    );
  }

  return (
    <span
      className={`inline-flex items-center gap-1.5 rounded-full border px-2 py-0.5 ${s.pill} ${className}`}
      data-testid={`presence-dot-${userId}`}
      data-status={status}
      title={`${s.label} — presence in portal, not session mode`}
    >
      <span
        aria-hidden
        className={`${s.dot} ring-2 ${s.ring} ${status === "available" ? "animate-pulse" : ""}`}
        style={{ width: 8, height: 8, borderRadius: 999, display: "inline-block" }}
      />
      <span className={`${compact ? "text-[10px]" : "text-xs"} font-bold uppercase tracking-wider`}>
        {s.label}
      </span>
    </span>
  );
}
